import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { copy } from "@/content";

const HERO_IMAGE =
  "https://images.pexels.com/photos/30412656/pexels-photo-30412656.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lineVariants = {
  hidden: { y: "115%", rotate: 3 },
  visible: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { delay: 0.35 + i * 0.13, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { lang } = useLanguage();
  const t = copy[lang].hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden border-b border-white/10" data-testid="hero-section">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-12 px-6 pb-24 pt-36 md:grid-cols-12 md:px-12 md:pt-44">
        <motion.div style={{ y: textY }} className="md:col-span-8" key={lang}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 font-tty text-xs uppercase tracking-[0.3em] text-acid"
            data-testid="hero-kicker"
          >
            {t.kicker}
          </motion.p>

          <h1 className="font-display text-5xl font-medium leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-8xl" data-testid="hero-headline">
            {t.lines.map((segments, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block origin-left will-change-transform"
                >
                  {segments.map((seg, j) =>
                    seg.italic ? (
                      <em key={j} className="italic text-acid">{seg.text}</em>
                    ) : (
                      <span key={j}>{seg.text}</span>
                    )
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 max-w-xl font-tty text-sm leading-relaxed text-neutral-400 md:text-base"
            data-testid="hero-subtitle"
          >
            {t.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a
              href="#servicos"
              data-testid="hero-cta-primary"
              className="group flex items-center gap-2 bg-acid px-8 py-4 font-tty text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
            >
              {t.ctaPrimary}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#contacto"
              data-testid="hero-cta-secondary"
              className="border border-white/20 px-8 py-4 font-tty text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-acid hover:text-acid"
            >
              {t.ctaSecondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 flex items-center gap-5"
          >
            <div className="border border-white/15 p-4 transition-colors duration-500 hover:border-acid/50" data-testid="hero-brand-logo">
              <img src="/assets/libertec-logo-dark.png" alt="Libertec — logótipo" className="h-36 w-auto" />
            </div>
            <p className="max-w-[140px] font-tty text-[10px] uppercase leading-relaxed tracking-[0.25em] text-neutral-500">
              {t.brandNote}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="hidden md:col-span-4 md:block"
        >
          <motion.div style={{ y: imageY }} className="relative">
            <div className="overflow-hidden border border-white/15">
              <img
                src={HERO_IMAGE}
                alt="Abstract open infrastructure"
                className="aspect-[3/4] w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
                data-testid="hero-image"
              />
            </div>
            <p className="mt-3 font-tty text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              {t.imageCaption}
            </p>
            <div className="absolute -left-3 -top-3 h-6 w-6 border-l border-t border-acid" />
            <div className="absolute -bottom-3 -right-3 h-6 w-6 border-b border-r border-acid" />
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 md:left-12"
        data-testid="hero-scroll-hint"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-acid" />
        <span className="font-tty text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t.scrollHint}</span>
      </motion.div>
    </section>
  );
};
